// client/src/pages/AchievementsPage.jsx
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Container,
  LinearProgress
} from '@mui/material';
import { MilitaryTech, ArrowBack, EmojiEvents } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

export default function AchievementsPage() {
  const navigate = useNavigate();
  const [badgesData, setBadgesData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBadgesData = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('https://propertyearn.net/api/api/user/badges', {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setBadgesData(data);
        } else {
          console.error('Failed to fetch badges data');
        }
      } catch (err) {
        console.error('Failed to fetch badges:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBadgesData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/dashboard')}
          sx={{ mb: 2 }}
        >
          Back to Dashboard
        </Button>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <MilitaryTech color="primary" sx={{ mr: 2, fontSize: 40 }} />
          <Box>
            <Typography variant="h4" gutterBottom>
              Your Achievements
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Track your progress and unlock new badges
            </Typography>
          </Box>
          {badgesData && (
            <Chip 
              label={`${badgesData.total_badges || 0} Badges Earned`} 
              color="primary" 
              sx={{ ml: 'auto', fontSize: '1rem', py: 1 }}
            />
          )}
        </Box>
      </Box>

      {!badgesData ? (
        <Alert severity="error">Failed to load achievements data</Alert>
      ) : (
        <Box>
          {/* Earned Badges Section */}
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h5" gutterBottom color="primary">
              🏆 Earned Badges
            </Typography>
            {badgesData.badges && badgesData.badges.length > 0 ? (
              <Grid container spacing={2}>
                {badgesData.badges.map((badge) => (
                  <Grid item xs={12} sm={6} md={4} key={badge.id}>
                    <Paper 
                      sx={{ 
                        p: 3, 
                        textAlign: 'center',
                        backgroundColor: `${badge.color}.light`,
                        border: `3px solid`,
                        borderColor: `${badge.color}.main`,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      <Typography variant="h2" sx={{ mb: 1 }}>
                        {badge.icon}
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {badge.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {badge.description}
                      </Typography>
                      <Chip 
                        label="Earned" 
                        color="success" 
                        size="small" 
                      />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <EmojiEvents sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Badges Yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Complete tasks to earn your first badge!
                </Typography>
                <Button 
                  variant="contained" 
                  onClick={() => navigate('/tasks')}
                >
                  Start Completing Tasks
                </Button>
              </Box>
            )}
          </Paper>

          {/* Next Badges Section */}
          {badgesData.next_badges && badgesData.next_badges.length > 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom color="primary">
                🎯 Next Badges
              </Typography>
              <Grid container spacing={3}>
                {badgesData.next_badges.map((badge, index) => (
                  <Grid item xs={12} key={index}>
                    <Paper sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h4" sx={{ mr: 2 }}>
                          {badge.icon}
                        </Typography>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" fontWeight="bold">
                            {badge.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {badge.description}
                          </Typography>
                        </Box>
                        <Typography variant="body2" fontWeight="bold">
                          {badge.progress}/{badge.target}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(badge.progress / badge.target) * 100}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        {badge.target - badge.progress} more to go
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}
        </Box>
      )}
    </Container>
  );
}